import React from 'react';

import { FieldErrors, UseFormRegister } from 'react-hook-form';
import * as yup from 'yup';

import DeleteIcon from '@mui/icons-material/Delete';
import { Box, IconButton, Stack, TextField } from '@mui/material';

import { invoiceCreateSchema } from '~/components/InvoiceTools/schema/invoice.schema';

type InvoiceCreate = yup.InferType<typeof invoiceCreateSchema>;

interface LineItemFieldProps {
  index: number;
  register: UseFormRegister<InvoiceCreate>;
  errors: FieldErrors<InvoiceCreate>;
  onRemove: () => void;
  canRemove: boolean;
  disabled?: boolean;
}

export default function LineItemField({ index, register, errors, onRemove, canRemove, disabled }: LineItemFieldProps) {
  const lineItemErrors = errors.lineItems?.[index];

  return (
    <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1, mb: 2 }}>
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box sx={{ typography: 'subtitle2', color: 'text.secondary' }}>Line Item {index + 1}</Box>
          {canRemove && (
            <IconButton color="error" onClick={onRemove} disabled={disabled} size="small">
              <DeleteIcon />
            </IconButton>
          )}
        </Stack>

        <TextField
          {...register(`lineItems.${index}.description`)}
          label="Description"
          multiline
          rows={5}
          fullWidth
          required
          disabled={disabled}
          error={!!lineItemErrors?.description}
          helperText={lineItemErrors?.description?.message}
          slotProps={{ inputLabel: { shrink: true } }}
        />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            {...register(`lineItems.${index}.bookingNumber`)}
            label="Booking Number"
            fullWidth
            required
            disabled={disabled}
            error={!!lineItemErrors?.bookingNumber}
            helperText={lineItemErrors?.bookingNumber?.message}
            slotProps={{ inputLabel: { shrink: true } }}
          />

          <TextField
            {...register(`lineItems.${index}.totalPrice`, { valueAsNumber: true })}
            label="Total Price"
            type="number"
            fullWidth
            required
            disabled={disabled}
            error={!!lineItemErrors?.totalPrice}
            helperText={lineItemErrors?.totalPrice?.message}
            slotProps={{ inputLabel: { shrink: true } }}
            inputProps={{ step: '0.01', min: '0' }}
          />

          <TextField
            {...register(`lineItems.${index}.gstAmount`, { valueAsNumber: true })}
            label="GST Amount"
            type="number"
            fullWidth
            required
            disabled={disabled}
            error={!!lineItemErrors?.gstAmount}
            helperText={lineItemErrors?.gstAmount?.message}
            slotProps={{ inputLabel: { shrink: true } }}
            inputProps={{ step: '0.01', min: '0' }}
          />
        </Stack>
      </Stack>
    </Box>
  );
}
